import {
  Box,
  Typography,
  Paper,
  TextField,
  useTheme,
  Divider,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogActions,
} from "@mui/material";
import {
  Search,
  CheckCircle,
  Cancel,
  Undo,
  ListAlt,
  Person,
  Person2,
} from "@mui/icons-material";
import { useState } from "react";
import { ControlCenterRoundContainer } from "./ControlCenterComponents";
import { StandardButton } from "../../components/StandardButton";
import { Tables } from "../../../database.types";
import { gameService } from "../../data/gameService";
import { useSupabase } from "../../auth/useSupabase";

interface ClassicBonusRoundAdminProps {
  bonusRound: Tables<"bonus_rounds">;
  classicBonusAnswers: Tables<"classic_bonus_answers">[];
  classicBonusTeamResponses: Tables<"classic_bonus_team_responses">[];
  team1Id: string;
  team2Id: string;
  team1Name: string;
  team2Name: string;
  gameId: string;
  gameState: Tables<"game_state">;
  refetchData: () => Promise<void>;
}

const ClassicBonusRoundAdmin = (props: ClassicBonusRoundAdminProps) => {
  const {
    bonusRound,
    classicBonusAnswers,
    classicBonusTeamResponses,
    team1Id,
    team2Id,
    team1Name,
    team2Name,
    gameId,
    gameState,
    refetchData,
  } = props;

  const theme = useTheme();
  const { supabase } = useSupabase();

  const [answerSearchQuery, setAnswerSearchQuery] = useState<string>("");
  const [isConfirmRevealAll, setIsConfirmRevealAll] = useState<boolean>(false);

  // Derive data from props
  const team1Responses = classicBonusTeamResponses
    .filter((r) => r.team_id === team1Id)
    .sort((a, b) => a.response_order - b.response_order);

  const team2Responses = classicBonusTeamResponses
    .filter((r) => r.team_id === team2Id)
    .sort((a, b) => a.response_order - b.response_order);

  const isTeam1Turn = team1Responses.length === team2Responses.length;
  const currentRoundIdx =
    gameState.current_phase === "overtime" ? "overtime" : "bonus";

  const onSelectAnswer = async (answer: Tables<"classic_bonus_answers">) => {
    const currentTeamId = isTeam1Turn ? team1Id : team2Id;
    const currentTeamResponses = isTeam1Turn ? team1Responses : team2Responses;

    await gameService.updateClassicBonusAnswer(supabase, answer.id, {
      correct_team_id: currentTeamId,
    });
    await gameService.addClassicBonusTeamResponse(supabase, {
      bonus_round_id: bonusRound.id,
      team_id: currentTeamId,
      response_order: currentTeamResponses.length,
      response_text: answer.answer_text,
    });
    await gameService.updateGameState(supabase, gameId, {
      updated_at: new Date().toISOString(),
    });
    await refetchData();
  };

  const onIncorrectAnswer = async () => {
    const currentTeamId = isTeam1Turn ? team1Id : team2Id;
    const currentTeamResponses = isTeam1Turn ? team1Responses : team2Responses;

    await gameService.addClassicBonusTeamResponse(supabase, {
      bonus_round_id: bonusRound.id,
      team_id: currentTeamId,
      response_order: currentTeamResponses.length,
      response_text: "",
    });
    await gameService.updateGameState(supabase, gameId, {
      updated_at: new Date().toISOString(),
    });
    await refetchData();
  };

  const onUndo = async () => {
    // Undo the last response (opposite of current turn)
    const undoTeamId = isTeam1Turn ? team2Id : team1Id;
    const undoTeamResponses = isTeam1Turn ? team2Responses : team1Responses;

    if (undoTeamResponses.length === 0) return;

    const lastResponse = undoTeamResponses[undoTeamResponses.length - 1];

    // If the response was a correct answer, clear the correct_team_id on the matching answer
    if (lastResponse.response_text) {
      const matchingAnswer = classicBonusAnswers.find(
        (a) => a.answer_text === lastResponse.response_text,
      );
      if (matchingAnswer) {
        await gameService.updateClassicBonusAnswer(
          supabase,
          matchingAnswer.id,
          { correct_team_id: null },
        );
      }
    }

    await gameService.removeLastClassicBonusTeamResponse(
      supabase,
      bonusRound.id,
      undoTeamId,
    );
    await gameService.updateGameState(supabase, gameId, {
      updated_at: new Date().toISOString(),
    });
    await refetchData();
  };

  const onToggleShowQuestion = async () => {
    await gameService.updateBonusRound(supabase, bonusRound.id, {
      show_question: !bonusRound.show_question,
    });
    await gameService.updateGameState(supabase, gameId, {
      updated_at: new Date().toISOString(),
    });
    await refetchData();
  };

  const onRevealAll = async () => {
    await gameService.updateBonusRound(supabase, bonusRound.id, {
      is_reveal_all: true,
    });
    await gameService.updateGameState(supabase, gameId, {
      updated_at: new Date().toISOString(),
    });
    await refetchData();
  };

  const onUndoRevealAll = async () => {
    await gameService.updateBonusRound(supabase, bonusRound.id, {
      is_reveal_all: false,
    });
    await gameService.updateGameState(supabase, gameId, {
      updated_at: new Date().toISOString(),
    });
    await refetchData();
  };

  const onClickNext = async () => {
    await gameService.navigateToRound(supabase, gameId, 7);
    await refetchData();
  };

  const onClickPrevious = async () => {
    if (currentRoundIdx === "overtime") {
      await gameService.navigateToRound(supabase, gameId, 12);
    } else {
      await gameService.navigateToRound(supabase, gameId, 6);
    }
    await refetchData();
  };

  return (
    <ControlCenterRoundContainer
      onClickNext={onClickNext}
      onClickPrevious={onClickPrevious}
      currentRoundIdx={currentRoundIdx}
    >
      <Paper
        elevation={3}
        sx={{
          display: "flex",
          width: "100%",
          flexDirection: "column",
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          mb: 3,
          gap: "12px",
        }}
      >
        <Typography
          variant="h5"
          component="h2"
          fontWeight="bold"
          color="primary"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 3,
          }}
        >
          <ListAlt />
          Classic Bonus Round
        </Typography>

        {bonusRound.show_question ? (
          <Typography
            variant="h3"
            component="h2"
            fontWeight="bold"
            onClick={onToggleShowQuestion}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 3,
            }}
          >
            {bonusRound.question_text}
          </Typography>
        ) : (
          <StandardButton
            fullWidth
            onClick={onToggleShowQuestion}
            size="large"
            variant="outlined"
          >
            Show Question
          </StandardButton>
        )}

        {/* Team Turn Indicator */}
        <Paper
          elevation={2}
          sx={{
            display: "flex",
            mb: 3,
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
              bgcolor: isTeam1Turn
                ? theme.palette.primary.main
                : theme.palette.background.paper,
              color: isTeam1Turn ? "white" : theme.palette.text.primary,
              transition: "all 0.3s ease",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Person />
              <Typography fontWeight={isTeam1Turn ? "bold" : "normal"}>
                {team1Name}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
              bgcolor: !isTeam1Turn
                ? theme.palette.secondary.main
                : theme.palette.background.paper,
              color: !isTeam1Turn ? "white" : theme.palette.text.primary,
              transition: "all 0.3s ease",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Person2 />
              <Typography fontWeight={!isTeam1Turn ? "bold" : "normal"}>
                {team2Name}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Main Content */}
        <Box
          sx={{
            display: "flex",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          {/* Team 1 Score Tracker */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              p: 1,
              flexBasis: "60px",
              flexShrink: 0,
              bgcolor: theme.palette.background.default,
              borderRight: `1px solid ${theme.palette.divider}`,
            }}
          >
            {team1Responses.map((r, index) => (
              <Box
                key={index}
                sx={{
                  height: "30px",
                  width: "30px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: r.response_text ? "success.main" : "error.main",
                  color: "white",
                }}
              >
                {r.response_text ? (
                  <CheckCircle fontSize="small" />
                ) : (
                  <Cancel fontSize="small" />
                )}
              </Box>
            ))}
          </Box>

          {/* Main Answer Selection Area */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              alignItems: "center",
              width: "100%",
              p: 2,
              bgcolor: theme.palette.background.paper,
            }}
          >
            <TextField
              value={answerSearchQuery}
              onChange={(e) => setAnswerSearchQuery(e.target.value)}
              fullWidth
              variant="outlined"
              placeholder="Search answers..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="primary" />
                  </InputAdornment>
                ),
              }}
            />

            <Divider flexItem />

            <Box
              sx={{
                width: "100%",
                overflow: "auto",
                maxHeight: "300px",
                p: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  flexWrap: "wrap",
                  gap: 1,
                  width: "100%",
                }}
              >
                {classicBonusAnswers.map((answer) => {
                  if (
                    answerSearchQuery &&
                    !answer.answer_text
                      .toLowerCase()
                      .includes(answerSearchQuery.toLowerCase())
                  ) {
                    return null;
                  }

                  return (
                    <StandardButton
                      key={answer.id}
                      variant={
                        answer.correct_team_id ? "contained" : "outlined"
                      }
                      color={
                        answer.correct_team_id === team1Id
                          ? "primary"
                          : answer.correct_team_id === team2Id
                            ? "secondary"
                            : "inherit"
                      }
                      onClick={() => onSelectAnswer(answer)}
                      disabled={!!answer.correct_team_id}
                      sx={{
                        height: "60px",
                        width: { xs: "100%", sm: "45%", md: "30%" },
                        textTransform: "none",
                        fontWeight: "medium",
                      }}
                    >
                      {answer.answer_text}
                    </StandardButton>
                  );
                })}
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                width: "100%",
                gap: 2,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <StandardButton
                fullWidth
                onClick={onIncorrectAnswer}
                size="large"
                variant="contained"
                color="error"
                startIcon={<Cancel />}
              >
                Incorrect Answer
              </StandardButton>

              <StandardButton
                fullWidth
                onClick={onUndo}
                size="large"
                variant="outlined"
                color="warning"
                startIcon={<Undo />}
              >
                Undo
              </StandardButton>
            </Box>
            <StandardButton
              variant="outlined"
              fullWidth
              onClick={() => {
                if (!bonusRound.is_reveal_all) {
                  setIsConfirmRevealAll(true);
                } else {
                  onUndoRevealAll();
                }
              }}
            >
              {bonusRound.is_reveal_all ? "Undo Reveal All" : "Reveal All"}
            </StandardButton>
          </Box>

          {/* Team 2 Score Tracker */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              p: 1,
              flexBasis: "60px",
              flexShrink: 0,
              bgcolor: theme.palette.background.default,
              borderLeft: `1px solid ${theme.palette.divider}`,
            }}
          >
            {team2Responses.map((r, index) => (
              <Box
                key={index}
                sx={{
                  height: "30px",
                  width: "30px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: r.response_text ? "success.main" : "error.main",
                  color: "white",
                }}
              >
                {r.response_text ? (
                  <CheckCircle fontSize="small" />
                ) : (
                  <Cancel fontSize="small" />
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>
      <Dialog open={isConfirmRevealAll}>
        <DialogTitle>Are you sure you want to reveal all?</DialogTitle>
        <DialogActions>
          <StandardButton
            variant="text"
            onClick={() => setIsConfirmRevealAll(false)}
          >
            Cancel
          </StandardButton>
          <StandardButton
            variant="contained"
            onClick={() => {
              setIsConfirmRevealAll(false);
              onRevealAll();
            }}
          >
            Yes, reveal all
          </StandardButton>
        </DialogActions>
      </Dialog>
    </ControlCenterRoundContainer>
  );
};

export default ClassicBonusRoundAdmin;
